/**
 * The presentation's podium, driven by the live script (TIBBS-VOICE.md 4).
 * Pure: the places come from the final totals (the same slot rules as
 * readout/facts.ts), and each step rises when Tibbs reaches the sentence that
 * names its prize.
 */
import type { GameState } from '../../engine';
import { prizeCues, scriptSentences } from '../../readout/prizes';
import type { PrizeKey } from '../../readout/prizes';

export interface Podium {
  gold: number[];
  silver: number[];
  bronze: number[];
  last: number[];
}

/** Places from the regulation totals, with the same rules as readout/facts.ts: the Golden Amphora, the Silver Laurel (place 2), the Bronze (place 3, 4+ players), the Pompeii Prize (last). */
export function podiumSeats(state: Pick<GameState, 'players' | 'winners'>): Podium {
  const totals = state.players.map((p) => p.holeScores.reduce((a, v) => a + v, 0));
  const n = totals.length;
  const seats = totals.map((_, i) => i);
  // A playoff decides a tied lead: the engine's winners rank first.
  const win = state.winners?.length && state.winners.length < n ? new Set(state.winners) : null;
  const place = (i: number) =>
    win ? (win.has(i) ? 1 : 1 + win.size + seats.filter((x) => !win.has(x) && totals[x]! < totals[i]!).length) : 1 + seats.filter((x) => totals[x]! < totals[i]!).length;
  const losers = win ? seats.filter((i) => !win.has(i)) : seats;
  const worst = Math.max(...losers.map((i) => totals[i]!));
  const allTied = !win && Math.min(...totals) === worst;
  const isLast = (i: number) => !allTied && !win?.has(i) && totals[i] === worst;
  const out: Podium = { gold: [], silver: [], bronze: [], last: [] };
  for (const i of seats) {
    const p = place(i);
    if (p === 1) out.gold.push(i);
    else if (isLast(i)) out.last.push(i);
    else if (p === 2) out.silver.push(i);
    else if (p === 3 && n >= 4) out.bronze.push(i);
  }
  return out;
}

export interface Reveal {
  crest: boolean;
  last: boolean;
  bronze: boolean;
  silver: boolean;
  gold: boolean;
}

/**
 * Which steps are up, given the script's sentences so far and the sentence
 * Tibbs is saying. Prizes are found in presentation order, each after the one
 * before, skipping the opener (which names the Golden Amphora), by the same
 * cues the server checks every presentation for (readout/prizes.ts).
 */
export function revealAt(sentences: string[], saying: number, done: boolean, podium: Podium): Reveal {
  if (done) return { crest: true, last: true, bronze: true, silver: true, gold: true };
  const at = prizeCues(sentences, (p) => podium[p].length > 0);
  const up = (p: PrizeKey) => at[p] !== undefined && saying >= at[p]!;
  return { crest: saying >= 0, last: up('last'), bronze: up('bronze'), silver: up('silver'), gold: up('gold') };
}

export { scriptSentences };
